import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { Slide } from '../components/SlideComponents/slide.types';
import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import backendService from '../services/backendService';
import { CropConfig } from '../services/backendService/types';

const findAllPrefetchableMediaElements = (slide: Slide) => {
  const mediaElements = slide.elements.filter(e => {
    if (!e.mediaFileId) return false;
    // no need to prefetch iframes
    if (e.mediaMimetype === 'iframe/video') return false;
    // no need to prefetch gifs from giphy they are faaast
    if (e.mediaMimetype === 'giphy/gif') return false;
    return true;
  });

  return mediaElements;
};

/**
 * Used by the host to download images from the socket api
 * TODO: rewrite cropped image so support sockets
 */
export const HostPresentationMediaPrefetcher = ({
  slides,
  tenantId,
}: {
  slides?: Slide[] | null;
  tenantId: string;
}) => {
  const queryClient = useQueryClient();
  const { gameAPI } = useContext(SocketContext);

  useEffect(() => {
    if (slides && gameAPI) {
      const fn = async () => {
        await Promise.all(
          slides.map(async slide => {
            if (slide.backgroundMedia) {
              if (slide.backgroundMedia.fileMimeType === 'image/unsplash') {
                await backendService.instance.getWebImage(slide.backgroundMedia?.fileId);
              }
            }
            const allMediaElements = findAllPrefetchableMediaElements(slide);

            await Promise.all(
              allMediaElements.map(async mediaElement => {
                if (mediaElement.mediaMimetype === 'image/unsplash') {
                  await backendService.instance.getWebImage(mediaElement.mediaFileId);
                  return;
                }

                queryClient.prefetchQuery(
                  createGameImageCacheKey(
                    mediaElement.mediaFileId,
                    mediaElement.mediaCropConfig,
                  ),
                  async () => {
                    if (mediaElement.mediaFileId) {
                      if (mediaElement.mediaMimetype === 'image/gif') {
                        return gameAPI.game.getDownloadUrl({
                          pathname: `game_files/${mediaElement.mediaFileId}`,
                          tenantId,
                        });
                      } else if (mediaElement.mediaMimetype === 'image/svg+xml') {
                        return backendService.instance.gameFiles.fetchSvgImage({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: false,
                          isAuthenticated: true,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner: true,
                        });
                      } else {
                        return backendService.instance.gameFiles.fetchCroppedImageV2({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: false,
                          isAuthenticated: true,
                          isOwner: true,
                          cropConfig: mediaElement.mediaCropConfig,
                        });
                      }
                    }
                    return Promise.resolve(undefined);
                  },
                  { staleTime: 1000 * 60 * 60 },
                );
              }),
            );
          }),
        );
      };
      fn();
    }
  }, [slides, gameAPI, queryClient, tenantId]);
  return null;
};

export const createGameImageCacheKey = (
  fileId?: string | null,
  cropConfig?: CropConfig | null,
) => {
  return ['game_image', fileId, cropConfig];
};

/**
 *
 * @param Handles private and public - but mainly for http image fetching
 * Used by lesson preview, admin lesson preview and in discover
 */
export const LessonMediaPreFetcher = ({
  slides,
  isAuthenticated,
  isDiscoveredTemplate,
  useThumbnail,
  isOwner,
}: {
  slides?: Slide[];
  isAuthenticated: boolean;
  isDiscoveredTemplate: boolean;
  useThumbnail?: boolean;
  isOwner: boolean;
}) => {
  const queryClient = useQueryClient();
  // Live game Viewer
  useEffect(() => {
    if (slides && isAuthenticated === false && !isDiscoveredTemplate) {
      const fn = async () => {
        await Promise.all(
          slides.map(async slide => {
            if (slide.backgroundMedia) {
              if (slide.backgroundMedia.fileMimeType === 'image/unsplash') {
                await backendService.instance.getWebImage(
                  useThumbnail && slide.backgroundMedia?.thumbnailSrc
                    ? slide.backgroundMedia?.thumbnailSrc
                    : slide.backgroundMedia.fileId,
                );
              }
            }
            const allMediaElements = findAllPrefetchableMediaElements(slide);
            await Promise.all(
              allMediaElements.map(async mediaElement => {
                if (mediaElement.mediaMimetype === 'image/unsplash') {
                  await backendService.instance.getWebImage(
                    useThumbnail && mediaElement?.mediaThumbnailUrl
                      ? mediaElement.mediaThumbnailUrl
                      : mediaElement.mediaFileId,
                  );
                  return;
                }

                queryClient.prefetchQuery(
                  createGameImageCacheKey(
                    mediaElement.mediaFileId,
                    mediaElement.mediaCropConfig,
                  ),
                  async () => {
                    if (mediaElement.mediaFileId) {
                      if (mediaElement.mediaMimetype === 'image/gif') {
                        return backendService.instance.getPublicDownloadUrl(
                          `game_files/${mediaElement.mediaFileId}`,
                        );
                      } else if (mediaElement.mediaMimetype === 'image/svg+xml') {
                        return backendService.instance.gameFiles.fetchSvgImage({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: isDiscoveredTemplate,
                          isAuthenticated,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      } else {
                        return backendService.instance.gameFiles.fetchCroppedImageV2({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: isDiscoveredTemplate,
                          isAuthenticated,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      }
                    }
                    return Promise.resolve(undefined);
                  },
                  { staleTime: 1000 * 60 * 60 },
                );
              }),
            );
          }),
        );
      };
      fn();
    }
  }, [slides, queryClient, isAuthenticated, isDiscoveredTemplate, useThumbnail, isOwner]);

  useEffect(() => {
    if (slides && isAuthenticated === true && !isDiscoveredTemplate) {
      const fn = async () => {
        await Promise.all(
          slides.map(async slide => {
            if (slide.backgroundMedia) {
              if (slide.backgroundMedia.fileMimeType === 'image/unsplash') {
                await backendService.instance.getWebImage(
                  useThumbnail && slide.backgroundMedia?.thumbnailSrc
                    ? slide.backgroundMedia?.thumbnailSrc
                    : slide.backgroundMedia.fileId,
                );
              }
            }
            const allMediaElements = findAllPrefetchableMediaElements(slide);

            await Promise.all(
              allMediaElements.map(async mediaElement => {
                if (mediaElement.mediaMimetype === 'image/unsplash') {
                  await backendService.instance.getWebImage(mediaElement.mediaFileId);
                  return;
                }

                queryClient.prefetchQuery(
                  createGameImageCacheKey(
                    mediaElement.mediaFileId,
                    mediaElement.mediaCropConfig,
                  ),
                  async () => {
                    if (mediaElement.mediaFileId) {
                      if (mediaElement.mediaMimetype === 'image/gif') {
                        try {
                          const objUrl = await backendService.instance.gameFiles.getGifImage(
                            mediaElement.mediaFileId,
                          );
                          return objUrl;
                        } catch (e) {
                          return undefined;
                        }
                      } else if (mediaElement.mediaMimetype === 'image/svg+xml') {
                        return backendService.instance.gameFiles.fetchSvgImage({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: isDiscoveredTemplate,
                          isAuthenticated,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      } else {
                        return backendService.instance.gameFiles.fetchCroppedImageV2({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: false,
                          isAuthenticated: true,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      }
                    }
                    return Promise.resolve(undefined);
                  },
                  { staleTime: 1000 * 60 * 60 },
                );
              }),
            );
          }),
        );
      };
      fn();
    }
  }, [queryClient, slides, isAuthenticated, isDiscoveredTemplate, useThumbnail, isOwner]);

  useEffect(() => {
    if (slides && isDiscoveredTemplate) {
      const fn = async () => {
        await Promise.all(
          slides.map(async slide => {
            if (slide.backgroundMedia) {
              if (slide.backgroundMedia.fileMimeType === 'image/unsplash') {
                await backendService.instance.getWebImage(
                  useThumbnail && slide.backgroundMedia?.thumbnailSrc
                    ? slide.backgroundMedia.thumbnailSrc
                    : slide.backgroundMedia?.fileId,
                );
              }
            }
            const allMediaElements = findAllPrefetchableMediaElements(slide);

            await Promise.all(
              allMediaElements.map(async mediaElement => {
                if (mediaElement.mediaMimetype === 'image/unsplash') {
                  await backendService.instance.getWebImage(
                    useThumbnail
                      ? slide.backgroundMedia?.thumbnailSrc ||
                          slide.backgroundMedia?.fileId
                      : slide.backgroundMedia?.fileId,
                  );
                  return;
                }

                queryClient.prefetchQuery(
                  createGameImageCacheKey(
                    mediaElement.mediaFileId,
                    mediaElement.mediaCropConfig,
                  ),
                  async () => {
                    if (mediaElement.mediaFileId) {
                      if (mediaElement.mediaMimetype === 'image/gif') {
                        try {
                          const objUrl = await backendService.instance.gameFiles.getGifImage(
                            mediaElement.mediaFileId,
                          );
                          return objUrl;
                        } catch (e) {
                          return undefined;
                        }
                      } else if (mediaElement.mediaMimetype === 'image/svg+xml') {
                        return backendService.instance.gameFiles.fetchSvgImage({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: isDiscoveredTemplate,
                          isAuthenticated,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      } else {
                        return backendService.instance.gameFiles.fetchCroppedImageV2({
                          fileId: mediaElement.mediaFileId,
                          isFromDiscover: isDiscoveredTemplate,
                          cropConfig: mediaElement.mediaCropConfig,
                          isOwner,
                        });
                      }
                    }
                    return Promise.resolve(undefined);
                  },
                  { staleTime: 1000 * 60 * 60 },
                );
              }),
            );
          }),
        );
      };
      fn();
    }
  }, [queryClient, slides, isAuthenticated, isDiscoveredTemplate, useThumbnail, isOwner]);
  return null;
};
